import { useState } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SignaturePad from './SignaturePad';
import TrimbleAddressAutocomplete from '@/components/TrimbleAddressAutocomplete';
import { jsPDF } from 'jspdf';

const inputCls = "w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50";
const labelCls = "text-xs text-muted-foreground block mb-1";

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const RATING_ITEMS = [
  'The pre-trip inspection (As required by Sec. 392.7)',
  'Coupling and uncoupling of combination units. (If applicable)',
  "Familiar with vehicle's controls",
  'Placing the vehicle in operation',
  "Use of vehicle's controls",
  "Use of vehicle's emergency equipment",
  'Operating the vehicle in traffic',
  'Operating the vehicle while passing other vehicles',
  'Turning the vehicle',
  'Braking, and slowing the vehicle by means other than braking.',
  'Backing the vehicle',
  'Parking the vehicle',
];

const EQUIPMENT_CLASSES = ['Straight Truck', 'Truck-Tractor', 'Semi-Trailers', 'Doubles/Triples', 'Bus', 'Other'];

const RadioGroup = ({ value, onChange }) => (
  <div className="flex items-center gap-4">
    {['Pass', 'Fail', 'N/A'].map(opt => (
      <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
        <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${value === opt ? 'border-primary bg-primary' : 'border-border'}`}>
          {value === opt && <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
        </div>
        <input type="radio" className="hidden" checked={value === opt} onChange={() => onChange(opt)} />
        <span className="text-xs text-foreground">{opt}</span>
      </label>
    ))}
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className={labelCls}>{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>
    {children}
  </div>
);

const SectionHeader = ({ title }) => (
  <div className="bg-white/5 px-3 py-1.5 rounded text-xs font-bold text-foreground uppercase tracking-wider mt-4 mb-3">
    {title}
  </div>
);

export default function RoadTestExaminationModal({ driver, onClose, onSaved }) {
  const addrParts = (driver.address || '').split(',');
  const street = (addrParts[0] || '').trim();
  const city = (addrParts[1] || '').trim();
  const stateZip = (addrParts[2] || '').trim().split(' ');

  const [form, setForm] = useState({
    name: driver.full_name || '',
    address: street,
    city: city,
    state: stateZip[0] || driver.cdl_state || '',
    zip: stateZip[1] || '',
    ratings: Object.fromEntries(RATING_ITEMS.map(item => [item, 'Pass'])),
    other_explain: '',
    equipment: Object.fromEntries(EQUIPMENT_CLASSES.map(cls => [cls, { type: '', hazmat: '', miles: '' }])),
    remarks: '',
    examiner_signature: '',
    sign_date: new Date().toISOString().split('T')[0],
    examiner_name: '',
    road_test_date: new Date().toISOString().split('T')[0],
  });

  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setRating = (item, val) => setForm(p => ({ ...p, ratings: { ...p.ratings, [item]: val } }));
  const setEquip = (cls, field, val) => setForm(p => ({
    ...p,
    equipment: { ...p.equipment, [cls]: { ...p.equipment[cls], [field]: val } }
  }));

  const generatePdf = async () => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const W = 612, ml = 48, mr = 564, lh = 14;
    let y = 48;

    const txt = (str, x, yt, opts = {}) => {
      doc.setFontSize(opts.size || 9);
      doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
      doc.setTextColor(0, 0, 0);
      if (opts.align) doc.text(String(str || ''), x, yt, { align: opts.align });
      else doc.text(String(str || ''), x, yt);
    };
    const drawLine = (x1, y1, x2, y2) => { doc.setDrawColor(180, 180, 180); doc.line(x1, y1, x2, y2); };
    const underlined = (label, value, x, yy, w = 120) => {
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
      const lbl = `${label}:  `;
      doc.text(lbl, x, yy);
      const lw = doc.getTextWidth(lbl);
      doc.text(String(value || ''), x + lw, yy);
      doc.line(x + lw, yy + 1, x + lw + w, yy + 1);
    };

    // Header
    txt('GF Trucklines LLC', ml, y, { bold: true, size: 12 });
    txt('GF Trucklines LLC', mr, y, { bold: true, align: 'right', size: 9 }); y += lh;
    txt('6041 Thorne Bay Ct', mr, y, { align: 'right', size: 9 }); y += lh;
    txt('Las Vegas, NV 89110', mr, y, { align: 'right', size: 9 }); y += lh;
    txt('(702) 601-8300', mr, y, { align: 'right', size: 9 }); y += 28;

    txt("DRIVER'S ROAD TEST EXAMINATION", W / 2, y, { bold: true, size: 14, align: 'center' }); y += 22;

    underlined('Name', form.name, ml, y, 160); y += lh + 6;
    underlined('Address', form.address, ml, y, 130);
    underlined('City', form.city, ml + 290, y, 100); y += lh + 6;
    underlined('State', form.state, ml, y, 40);
    underlined('Zip', form.zip, ml + 130, y, 80); y += lh + 10;

    // Bold instruction paragraph
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
    const boldPart = 'The road test shall be given by the motor carrier or a person designated by the motor carrier. However, a driver who is a motor carrier must be given the test by another person. ';
    const normalPart = 'The test shall be given by a person who is competent to evaluate and determine whether the person who takes the test has demonstrated that he/she is capable of operating the vehicle and associated equipment that the motor carrier intends to assign.';
    const boldLines = doc.splitTextToSize(boldPart, mr - ml);
    doc.text(boldLines, ml, y); y += boldLines.length * lh;
    doc.setFont('helvetica', 'normal');
    const normalLines = doc.splitTextToSize(normalPart, mr - ml);
    doc.text(normalLines, ml, y); y += normalLines.length * lh + 14;

    // Rating of Performance header
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
    txt('Rating of Performance', ml, y);
    txt('Pass', ml + 380, y); txt('Fail', ml + 420, y); txt('N/A', ml + 460, y); y += lh + 4;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);

    for (const item of RATING_ITEMS) {
      const rating = form.ratings[item] || 'Pass';
      txt(item, ml, y);
      const passX = ml + 384, failX = ml + 424, naX = ml + 463;
      drawLine(passX - 8, y + 1, passX + 8, y + 1);
      drawLine(failX - 8, y + 1, failX + 8, y + 1);
      drawLine(naX - 8, y + 1, naX + 8, y + 1);
      if (rating === 'Pass') txt('X', passX - 2, y, { bold: true });
      else if (rating === 'Fail') txt('X', failX - 2, y, { bold: true });
      else if (rating === 'N/A') txt('X', naX - 2, y, { bold: true });
      y += lh + 2;
    }
    y += 6;
    txt('Other, Explain:', ml, y); y += lh + 2;
    // box for other explain
    doc.setDrawColor(180, 180, 180);
    doc.rect(ml, y, mr - ml, 20);
    if (form.other_explain) txt(form.other_explain, ml + 4, y + 13);
    y += 28;

    // Equipment table
    txt('Type of equipment used in giving test:', ml, y); y += lh + 4;
    doc.setDrawColor(0, 0, 0);
    const colX = [ml, ml + 130, ml + 350, ml + 430];
    const tableW = mr - ml;
    // header row
    doc.setFillColor(200, 200, 200);
    doc.rect(ml, y - 11, tableW, 16, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5);
    txt('Equipment Class', colX[0] + 2, y);
    txt('Equipment Type (Van, Flatbed, Tanker, Dump)', colX[1] + 2, y);
    txt('Hazmat Y/N', colX[2] + 2, y);
    txt('Approx Miles', colX[3] + 2, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    for (const cls of EQUIPMENT_CLASSES) {
      const eq = form.equipment[cls];
      doc.rect(ml, y - 10, tableW, 16);
      txt(cls, colX[0] + 2, y);
      txt(eq.type || '', colX[1] + 2, y);
      txt(eq.hazmat || '', colX[2] + 2, y);
      txt(eq.miles || '', colX[3] + 2, y);
      y += 16;
    }
    y += 10;
    txt('Remarks:', ml, y); y += lh + 2;
    doc.rect(ml, y, tableW, 40);
    if (form.remarks) txt(form.remarks, ml + 4, y + 13);
    y += 50;

    // Page 2
    doc.addPage(); y = 48;
    txt('Conducted By:', ml, y); y += 50;

    if (form.examiner_signature && form.examiner_signature.startsWith('data:')) {
      doc.addImage(form.examiner_signature, 'PNG', ml, y, 100, 40);
    }
    y += 50;
    drawLine(ml, y, ml + 200, y);
    drawLine(ml + 280, y, mr, y);
    y += 12;
    txt('Examiner Signature', ml, y);
    txt('Date Signed', ml + 280, y); y += 4;
    txt(form.sign_date, ml + 280, y - 16);
    y += 20;

    txt(form.examiner_name || '', ml, y);
    txt(form.road_test_date || '', ml + 280, y); y += 2;
    drawLine(ml, y, ml + 200, y);
    drawLine(ml + 280, y, mr, y); y += 12;
    txt('Examiner Name', ml, y);
    txt('Date of Road Test', ml + 280, y);

    // Footer on all pages
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(100);
      doc.text(new Date().toLocaleDateString(), ml, 780);
      doc.text('GF Trucklines LLC', W / 2, 780, { align: 'center' });
      doc.text(`Page ${i}`, mr, 780, { align: 'right' });
    }

    return doc;
  };

  const handleSave = async () => {
    if (!form.examiner_signature) {
      toast.error('Please provide an examiner signature.');
      return;
    }
    setSaving(true);
    toast.info('Generating Road Test PDF...');
    const doc = await generatePdf();
    const blob = doc.output('blob');
    const file = new File([blob], `road_test_${(driver.full_name || 'driver').replace(/\s+/g, '_')}.pdf`, { type: 'application/pdf' });
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    await client.entities.DriverProfile.update(driver.id, { dqf_road_test: true });
    toast.success('Road Test saved!');
    setSaving(false);
    if (onSaved) onSaved(file_url);
    onClose();
  };

  const handleDownload = async () => {
    const doc = await generatePdf();
    doc.save(`road_test_${(driver.full_name || 'driver').replace(/\s+/g, '_')}.pdf`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-3xl z-10 max-h-[92vh] flex flex-col overflow-hidden">

        <div className="flex items-start justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="font-space font-bold text-foreground text-lg">Driver's Road Test Examination</h2>
            <p className="text-xs text-muted-foreground mt-0.5">49 CFR 391.31</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground mt-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4 flex-1 text-sm">

          <SectionHeader title="Driver Information" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name" required><input value={form.name} onChange={e => set('name', e.target.value)} className={inputCls} /></Field>
            <div />
            <TrimbleAddressAutocomplete
              value={form.address}
              onChange={e => set('address', e.target.value)}
              onSelect={data => {
                set('address', data.street || form.address);
                if (data.city) set('city', data.city);
                if (data.state) set('state', data.state);
                if (data.zip) set('zip', data.zip);
              }}
              label="Address *"
            />
            <Field label="City" required><input value={form.city} onChange={e => set('city', e.target.value)} className={inputCls} /></Field>
            <Field label="State" required>
              <select value={form.state} onChange={e => set('state', e.target.value)} className={inputCls}>
                <option value="" className="bg-background">Select…</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </Field>
            <Field label="Postal Code" required><input value={form.zip} onChange={e => set('zip', e.target.value)} className={inputCls} /></Field>
          </div>

          <SectionHeader title="Rating of Performance" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground">The road test shall be given by the motor carrier or a person designated by the motor carrier. However, a driver who is a motor carrier must be given the test by another person.</strong> The test shall be given by a person who is competent to evaluate and determine whether the person who takes the test has demonstrated that he/she is capable of operating the vehicle and associated equipment that the motor carrier intends to assign.
          </p>
          <div className="space-y-1">
            {RATING_ITEMS.map(item => (
              <div key={item} className="flex items-center justify-between gap-3 py-2 border-b border-border">
                <span className="text-xs text-foreground flex-1">{item} <span className="text-red-400">*</span></span>
                <RadioGroup value={form.ratings[item]} onChange={val => setRating(item, val)} />
              </div>
            ))}
          </div>
          <Field label="Other, Explain:">
            <input value={form.other_explain} onChange={e => set('other_explain', e.target.value)} className={inputCls} />
          </Field>

          <SectionHeader title="Type of Equipment Used in Giving Test" />
          <div className="rounded-lg border border-border overflow-hidden text-xs">
            <div className="grid grid-cols-4 bg-white/5 px-3 py-2 font-semibold text-muted-foreground gap-2">
              <span>Equipment Class</span>
              <span>Equipment Type (Van, Flatbed, Tanker, Dump)</span>
              <span>Hazmat Y/N</span>
              <span>Approx Miles</span>
            </div>
            {EQUIPMENT_CLASSES.map(cls => (
              <div key={cls} className="grid grid-cols-4 gap-2 px-3 py-2 border-t border-border items-center">
                <span className="text-foreground">{cls}</span>
                <input value={form.equipment[cls].type} onChange={e => setEquip(cls, 'type', e.target.value)}
                  className="px-2 py-1 rounded bg-transparent border border-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/50" />
                <input value={form.equipment[cls].hazmat} onChange={e => setEquip(cls, 'hazmat', e.target.value)}
                  placeholder="Y / N"
                  className="px-2 py-1 rounded bg-transparent border border-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/50" />
                <input value={form.equipment[cls].miles} onChange={e => setEquip(cls, 'miles', e.target.value)}
                  className="px-2 py-1 rounded bg-transparent border border-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/50" />
              </div>
            ))}
          </div>

          <Field label="Remarks:">
            <textarea value={form.remarks} onChange={e => set('remarks', e.target.value)} rows={3} className={inputCls + ' resize-none'} />
          </Field>

          <SectionHeader title="Conducted By" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Examiner Name" required>
              <input value={form.examiner_name} onChange={e => set('examiner_name', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Date of Road Test" required>
              <input type="date" value={form.road_test_date} onChange={e => set('road_test_date', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Date Signed">
              <input type="date" value={form.sign_date} onChange={e => set('sign_date', e.target.value)} className={inputCls} />
            </Field>
          </div>
          <div>
            <label className={labelCls}>Examiner Signature <span className="text-red-400">*</span></label>
            <SignaturePad value={form.examiner_signature} onChange={v => set('examiner_signature', v)} />
          </div>

        </div>

        <div className="px-6 py-4 border-t border-border flex justify-between items-center gap-3 flex-shrink-0">
          <button type="button" onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
            Download PDF (preview)
          </button>
          <div className="flex gap-3">
            <Button variant="outline" className="border-border" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save & Upload</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}